import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Copy, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { logCodeAction } from "@/lib/codeAuditLog";

const MASKED_PLACEHOLDER = "••••••";

interface MaskedCodeProps {
  /** Valor atual do código (ex: TOTP de 6 dígitos ou corpo do email) */
  value: string;
  /** Para TOTP use 6 bolinhas; para texto longo use blur ou truncate */
  maskStyle?: "dots" | "blur";
  /** Usuário atual (para auditoria) */
  user: { id: string; email: string };
  /** Tipo do código para log */
  codeType: "totp" | "email";
  /** ID de referência (totp_codes.id ou email uid) */
  referenceId?: string | null;
  /** Rótulo para o log (nome do TOTP ou assunto do email) */
  codeLabel?: string | null;
  /** Chamado após copiar com sucesso (toast é exibido só se log for registrado) */
  onCopySuccess?: () => void;
  /** Classe extra no container do código */
  className?: string;
  /** Se true, mostra botão de copiar ao lado do código */
  showCopyButton?: boolean;
  /** Modo controlado: estado de revelação vindo do pai */
  revealed?: boolean;
  /** Modo controlado: chamado quando usuário pede para revelar (ex.: após log de auditoria) */
  onRevealRequest?: () => void | Promise<void>;
  /** Modo controlado: chamado quando usuário pede para ocultar */
  onHideRequest?: () => void;
}

export function MaskedCode({
  value,
  maskStyle = "dots",
  user,
  codeType,
  referenceId,
  codeLabel,
  onCopySuccess,
  className = "",
  showCopyButton = true,
  revealed: controlledRevealed,
  onRevealRequest,
  onHideRequest,
}: MaskedCodeProps) {
  const [internalRevealed, setInternalRevealed] = useState(false);
  const isControlled = controlledRevealed !== undefined;
  const revealed = isControlled ? controlledRevealed : internalRevealed;
  const { toast } = useToast();

  const handleReveal = () => {
    if (revealed) {
      if (isControlled) onHideRequest?.();
      else setInternalRevealed(false);
      return;
    }
    if (isControlled && onRevealRequest) {
      void Promise.resolve(onRevealRequest()).catch(() => {});
      return;
    }
    logCodeAction({
      userId: user.id,
      userEmail: user.email ?? "",
      action: "code_viewed",
      codeType,
      referenceId,
      codeLabel,
    }).finally(() => setInternalRevealed(true));
  };

  const handleCopy = async () => {
    try {
      await logCodeAction({
        userId: user.id,
        userEmail: user.email ?? "",
        action: "code_copied",
        codeType,
        referenceId,
        codeLabel,
      });
      await navigator.clipboard.writeText(value);
      toast({
        title: "Código copiado!",
        description: "O código foi copiado para a área de transferência",
      });
      onCopySuccess?.();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível registrar a cópia ou copiar o código.",
      });
    }
  };

  const isMasked = !revealed;

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <span
        className={
          isMasked && maskStyle === "blur"
            ? "select-none blur-md transition-all duration-200 font-mono tracking-wider max-w-full"
            : "font-mono tracking-wider max-w-full"
        }
        style={
          maskStyle === "dots" && !revealed
            ? { letterSpacing: "0.25em", fontSize: "1.875rem" }
            : maskStyle === "blur" && revealed
              ? { whiteSpace: "pre-wrap", wordBreak: "break-word" }
              : undefined
        }
      >
        {maskStyle === "dots" && !revealed
          ? MASKED_PLACEHOLDER
          : maskStyle === "blur" && !revealed
            ? MASKED_PLACEHOLDER
            : value}
      </span>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover:bg-accent"
          onClick={handleReveal}
          aria-label={revealed ? "Ocultar código" : "Mostrar código"}
        >
          {revealed ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </Button>
        {showCopyButton && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 hover:bg-accent"
            onClick={handleCopy}
            aria-label="Copiar código"
          >
            <Copy className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/** Versão para TOTP: valor numérico de 6 dígitos, máscara com bolinhas */
export function MaskedTOTP({
  value,
  user,
  codeId,
  codeName,
  onCopySuccess,
  className = "",
}: {
  value: string;
  user: { id: string; email: string };
  codeId: string;
  codeName: string;
  onCopySuccess?: () => void;
  className?: string;
}) {
  return (
    <MaskedCode
      value={value}
      maskStyle="dots"
      user={user}
      codeType="totp"
      referenceId={codeId}
      codeLabel={codeName}
      onCopySuccess={onCopySuccess}
      className={className}
      showCopyButton={true}
    />
  );
}

/** Email: apenas o assunto é exibido (mascarado ou revelado). Corpo NUNCA é renderizado no DOM; o botão copiar envia o corpo para a área de transferência. */
export function MaskedEmailCode({
  subject,
  emailUid,
  user,
  className = "",
}: {
  body: string;
  subject: string;
  emailUid: number;
  user: { id: string; email: string };
  className?: string;
}) {
  const [revealed, setRevealed] = useState(false);
  const { toast } = useToast();

  const handleReveal = () => {
    if (revealed) {
      setRevealed(false);
      return;
    }
    logCodeAction({
      userId: user.id,
      userEmail: user.email ?? "",
      action: "code_viewed",
      codeType: "email",
      referenceId: String(emailUid),
      codeLabel: subject,
    }).finally(() => setRevealed(true));
  };

  const handleCopy = async () => {
    const last6 = subject.slice(-6);
    if (last6.length !== 6 || !/^\d{6}$/.test(last6)) {
      toast({
        variant: "destructive",
        title: "Erro ao copiar",
        description: "Os 6 últimos caracteres do assunto não são numéricos.",
      });
      return;
    }
    try {
      await logCodeAction({
        userId: user.id,
        userEmail: user.email ?? "",
        action: "code_copied",
        codeType: "email",
        referenceId: String(emailUid),
        codeLabel: subject,
      });
      await navigator.clipboard.writeText(last6);
      toast({
        title: "Código copiado!",
        description: "Os 6 dígitos foram copiados para a área de transferência",
      });
    } catch {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível registrar a cópia ou copiar.",
      });
    }
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <p className="text-sm font-semibold text-foreground leading-snug">
        {revealed ? subject : MASKED_PLACEHOLDER}
      </p>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover:bg-accent"
          onClick={handleReveal}
          aria-label={revealed ? "Ocultar assunto" : "Mostrar assunto"}
        >
          {revealed ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover:bg-accent"
          onClick={handleCopy}
          aria-label="Copiar os 6 últimos dígitos do assunto"
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
